import { captureException, captureMessage, withScope } from '@sentry/vue';
import dayjs from 'dayjs';

import { parseUserAgent } from '@/shared/lib/user-agent';

import { getExceptionToCapture, normalizeErrors } from './normalize';
import { compactRecord, getNumber, isRecord, sanitizePayload } from './sanitize';
import {
  type BrowserConnectionInfo,
  type SentryAuthContext,
  type SentryAuthStoreLike,
  SentryLevel,
  type SentryOrganizationContext,
  type SentryPayload,
  SentryServiceName,
  type SentryUserContext,
} from './types';

const EXCLUDED_CLASSIFICATIONS = new Set([
  'InsufficientFundsException',
  'DocumentNumberAlreadyExistsException',
  'InvalidPaymentTypeForInternalAccountException',
  'IntraAndInterbankBicCorrespondenceException',
  'DEFAULT_PHONE_NUMBER_NOT_FOUND',
  'PAYER_EXCEPTION',
  'OperationNotValidException',
  'DuplicateSalePointNameException',
  'EntityNotFoundException',
  'TransactionCanNotBeRefundedException',
  'NotFoundQRPayment',
  'LimitBlockedException',
  'NOT_FOUND',
  'CreationDeadlineException',
  'INTRABANK_RKO_TO_CARD_NOT_ALLOWED',
  'AccountCreditClosedOrSeizedException',
]);

const EXCLUDED_ACTIONS = new Set(['getSalaryContractStatus']);
const SESSION_EXPIRES_AT_TIME_KEY = 'session-expires-at-time';

export { SentryLevel, SentryServiceName };
export type { SentryPayload } from './types';

export const buildSentryAuthContext = (authUserStore: SentryAuthStoreLike): SentryAuthContext => {
  const selectedUserContract = authUserStore.selectedUserContract;

  return {
    user: compactRecord({
      id: authUserStore.userId ? String(authUserStore.userId) : undefined,
      username: authUserStore.user?.fullName || undefined,
    }) as SentryUserContext | undefined,
    organization: isRecord(selectedUserContract)
      ? (compactRecord({
          org_reference_id: selectedUserContract.orgReferenceId,
          company_name: selectedUserContract.companyName,
          sign_type: selectedUserContract.signType,
          sign_scheme: selectedUserContract.signScheme,
          sign_right: isRecord(selectedUserContract.userContractAttributes)
            ? selectedUserContract.userContractAttributes.signRight
            : undefined,
        }) as SentryOrganizationContext | undefined)
      : undefined,
  };
};

export function useSentry(payloadData: SentryPayload) {
  const { error, typeAction, level = SentryLevel.ERROR } = payloadData;

  if (typeAction && EXCLUDED_ACTIONS.has(typeAction)) return;

  const normalizedErrors = normalizeErrors(error);
  const primaryError = normalizedErrors[0] ?? {
    source: 'unknown',
    message: 'unknown-error',
  };

  if (primaryError.classification && EXCLUDED_CLASSIFICATIONS.has(primaryError.classification)) return;
  const serviceCode = String(payloadData.service);
  const expiresAtUnix =
    typeof localStorage === 'undefined' ? undefined : getNumber(localStorage.getItem(SESSION_EXPIRES_AT_TIME_KEY));
  const lang = typeof localStorage === 'undefined' ? undefined : (localStorage.getItem('lang') ?? undefined);
  const { name, version, platform, userAgent } = parseUserAgent();
  const connection =
    (typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: BrowserConnectionInfo }).connection
      : undefined) ?? undefined;
  const user = payloadData.user ? compactRecord({ ...payloadData.user }) : undefined;
  const errorKey =
    primaryError.code ??
    primaryError.classification ??
    primaryError.path ??
    ((payloadData.statusCode ?? primaryError.statusCode)
      ? `http-${payloadData.statusCode ?? primaryError.statusCode}`
      : undefined) ??
    `${primaryError.source}-error`;
  const contexts = {
    organization: payloadData.organization ? compactRecord({ ...payloadData.organization }) : undefined,
    trace_info: compactRecord({
      service_code: serviceCode,
      frontend_action: typeAction,
      graphql_field: payloadData.graphqlField,
      x_trace_id: payloadData.xTraceId,
      graphql_classification: primaryError.classification,
      error_classification_header: payloadData.headerClassification,
      http_status: payloadData.statusCode ?? primaryError.statusCode,
      session_expires_at: expiresAtUnix ? dayjs.unix(expiresAtUnix).format('DD.MM.YYYY HH:mm:ss') : undefined,
      app_version: import.meta.env.VITE_APP_VERSION,
    }),
    browser: compactRecord({
      name,
      version,
      lang: lang ?? 'unknown',
      platform,
      user_agent: userAgent,
      screen_resolution: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : undefined,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      visibility_state: typeof document !== 'undefined' ? document.visibilityState : undefined,
      connection: compactRecord({
        downlink: connection?.downlink,
        effective_type: connection?.effectiveType,
        rtt: connection?.rtt,
        save_data: connection?.saveData,
      }),
    }),
  };
  const extras = compactRecord({
    payload: sanitizePayload(payloadData.payload),
    error_messages: normalizedErrors.map((entry) => entry.message),
  });
  const eventMessage = typeAction
    ? `${serviceCode} | ${typeAction}`
    : payloadData.graphqlField
      ? `${serviceCode} | ${payloadData.graphqlField}`
      : serviceCode;
  const exception = getExceptionToCapture(error);

  withScope((scope) => {
    scope.setLevel(level);
    scope.setFingerprint([
      serviceCode,
      typeAction ?? 'unknown-action',
      payloadData.graphqlField ?? 'unknown-field',
      errorKey,
    ]);
    scope.setTags(
      compactRecord({
        service: serviceCode,
        operation_name: typeAction,
        graphql_field: payloadData.graphqlField,
        graphql_classification: primaryError.classification,
      }) as Record<string, string>
    );

    if (user) scope.setUser(user);

    Object.entries(contexts).forEach(([key, value]) => {
      if (value) scope.setContext(key, value);
    });

    if (extras) scope.setExtras(extras);
    if (exception) return captureException(exception);

    captureMessage(eventMessage);
  });
}
